import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search, Activity, AlertTriangle, CalendarClock, TrendingDown,
  Users, ClipboardList, Wallet,
} from "lucide-react";

// The clinic board.
//
// It leads with what needs a DECISION - a plateau, a lapsing authorization, a
// patient falling behind their plan - and puts the totals underneath. A board
// that opens with "128 patients" tells nobody what to do next.

function Stat({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
    teal: "bg-teal-50 text-teal-800 border-teal-200",
  };
  return (
    <Card className={`p-4 border ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold tabular-nums">{value}</div>
      {sub ? <div className="mt-1 text-xs opacity-70">{sub}</div> : null}
    </Card>
  );
}

const BOARD_LIMIT = 8;

function WorkList({ title, hint, rows, empty, columns, onOpen, testid, moreHref }) {
  // A board is a worklist, not a dump - 49 rows in one card is a list nobody
  // works through. But the cap is NEVER silent: a truncated list that does not
  // say so reads as "that is all of them", which is the more expensive lie.
  const shown = rows.slice(0, BOARD_LIMIT);
  const hidden = rows.length - shown.length;
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b bg-white">
        <div className="font-bold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-500">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left font-semibold px-4 py-2 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.plan_id || r.id || i} className="border-t hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-2 whitespace-nowrap">
                      {c.render ? c.render(r) : (r[c.key] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => onOpen(r)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hidden > 0 && (
        <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-600 flex items-center justify-between">
          <span>
            Showing {shown.length} of <span className="font-semibold">{rows.length}</span>.
            {" "}{hidden} more not listed here.
          </span>
          {moreHref && (
            <Link to={moreHref} className="font-semibold text-teal-700 hover:underline">
              See all
            </Link>
          )}
        </div>
      )}
    </Card>
  );
}

export default function PhysioDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [board, setBoard] = useState(null);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const clinic = user?.active_hospital_name || user?.hospital_name || "this clinic";

  useEffect(() => {
    let dead = false;
    Promise.all([
      api.get("/physio/dashboard"),
      api.get("/patients").catch(() => ({ data: [] })),
    ])
      .then(([d, p]) => {
        if (dead) return;
        setBoard(d.data);
        setPatients(Array.isArray(p.data) ? p.data : p.data?.patients || []);
        setLoading(false);
      })
      .catch((e) => {
        if (dead) return;
        toast.error(formatErr(e?.response?.data?.detail));
        setLoading(false);
      });
    return () => { dead = true; };
  }, []);

  const found = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return patients
      .filter((p) => (p.full_name || "").toLowerCase().includes(t) ||
                     (p.contact_number || "").includes(t))
      .slice(0, 8);
  }, [q, patients]);

  if (loading) return <div className="p-8 text-slate-500">Loading the board…</div>;
  if (!board) return <div className="p-8 text-slate-500">No data.</div>;

  const na = board.needs_attention || {};
  const c = board.counts || {};
  const goals = board.goals || {};
  const openPlan = (r) => nav(`/physio/plans/${r.plan_id}`);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="physio-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
            Physical Therapy
          </h1>
          <p className="text-sm text-slate-500">{clinic}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/physio/floor"><Button variant="outline"><Activity className="h-4 w-4 mr-1.5" />Treatment floor</Button></Link>
          <Link to="/physio/caseload"><Button variant="outline"><Users className="h-4 w-4 mr-1.5" />Caseload</Button></Link>
        </div>
      </div>

      {/* Find a patient - the single most common action */}
      <Card className="p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Find a patient by name or mobile number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="physio-patient-search"
          />
        </div>
        {found.length > 0 && (
          <div className="mt-2 divide-y border rounded-md">
            {found.map((p) => (
              <button
                key={p.id}
                onClick={() => nav(`/physio/patients/${p.id}`)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex justify-between items-center"
              >
                <span className="font-medium text-slate-800">{p.full_name}</span>
                <span className="text-xs text-slate-500">
                  {p.age ? `${p.age}y` : ""} {p.gender || ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={ClipboardList} label="Active plans" value={board.active_plans} tone="slate" />
        <Stat icon={Activity} label="On the floor today" value={board.sessions_today}
              sub="sessions recorded today" tone="teal" />
        <Stat icon={AlertTriangle} label="Behind schedule" value={c.behind_schedule || 0}
              sub="attendance below plan" tone="amber" />
        <Stat icon={CalendarClock} label="Lapsed, unused" value={c.expired_unused || 0}
              sub="authorizations that expired with sessions left" tone="rose" />
      </div>

      <div className="text-xs text-slate-500">
        Goals across active plans: <span className="font-semibold text-slate-700">
          {goals.met ?? 0} of {goals.total ?? 0} met
        </span>
        {goals.met_pct != null ? ` (${goals.met_pct}%)` : ""} — derived from the latest
        measurements, not from a status anyone typed.
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <WorkList
          testid="physio-stalled"
          moreHref="/physio/caseload"
          title="Goals are not moving"
          hint="Re-measured, but the numbers have not changed. These need a change of plan, not more of the same."
          rows={na.stalled_goals || []}
          empty="Nothing stalled. Every re-measured goal is moving."
          onOpen={openPlan}
          columns={[
            { key: "patient_name", label: "Patient" },
            { key: "pt_diagnosis", label: "Diagnosis" },
            { key: "stalled_goals", label: "Stalled",
              render: (r) => <span className="font-semibold text-rose-700">{r.stalled_goals}</span> },
            { key: "overdue_goals", label: "Overdue" },
          ]}
        />

        <WorkList
          testid="physio-behind"
          moreHref="/physio/caseload"
          title="Falling behind the plan"
          hint="A course of treatment fails on attendance far more often than on clinical error."
          rows={na.behind_schedule || []}
          empty="Everyone is keeping to their plan."
          onOpen={openPlan}
          columns={[
            { key: "patient_name", label: "Patient" },
            { key: "pt_diagnosis", label: "Diagnosis" },
            { key: "completed", label: "Done / due",
              render: (r) => (
                <span className="tabular-nums">
                  <span className="font-semibold text-amber-700">{r.completed}</span>
                  <span className="text-slate-400"> / {r.expected}</span>
                </span>
              ) },
            { key: "attendance_rate_pct", label: "Attendance",
              render: (r) => r.attendance_rate_pct != null ? `${r.attendance_rate_pct}%` : "—" },
          ]}
        />

        <WorkList
          testid="physio-expiring"
          moreHref="/physio/balances"
          title="Authorizations expiring within 14 days"
          hint="Call the patient in, or start the renewal — after the date it is the clinic's loss."
          rows={na.balances_expiring_14d || []}
          empty="Nothing lapses in the next fortnight."
          onOpen={(r) => nav(`/physio/patients/${r.patient_id}`)}
          columns={[
            { key: "patient_name", label: "Patient" },
            { key: "funding_source", label: "Funded by",
              render: (r) => r.funding_source === "hmo"
                ? `${r.hmo_name || "HMO"} · ${r.loa_number || ""}` : "Self-paid package" },
            { key: "remaining", label: "Sessions left",
              render: (r) => <span className="font-semibold">{r.remaining}</span> },
            { key: "days_to_expiry", label: "Days left",
              render: (r) => <span className="text-amber-700 font-semibold">{r.days_to_expiry}</span> },
          ]}
        />

        <WorkList
          testid="physio-lapsed"
          moreHref="/physio/balances"
          title="Lapsed with sessions unused"
          hint="The clinic's quietest loss: an authorization that ran out while sessions were still on it."
          rows={na.expired_unused || []}
          empty="Nothing has lapsed unused."
          onOpen={(r) => nav(`/physio/patients/${r.patient_id}`)}
          columns={[
            { key: "patient_name", label: "Patient" },
            { key: "funding_source", label: "Funded by",
              render: (r) => r.funding_source === "hmo"
                ? `${r.hmo_name || "HMO"} · ${r.loa_number || ""}` : "Self-paid package" },
            { key: "remaining", label: "Unused",
              render: (r) => <span className="font-semibold text-rose-700">{r.remaining}</span> },
            { key: "valid_until", label: "Expired",
              render: (r) => (r.valid_until || "").slice(0, 10) },
          ]}
        />
      </div>

      <div className="flex gap-2">
        <Link to="/physio/balances">
          <Button variant="outline"><Wallet className="h-4 w-4 mr-1.5" />Packages &amp; authorizations</Button>
        </Link>
      </div>
    </div>
  );
}
